<!-- eslint-disable vue/no-v-html -->
<script setup lang="ts">
import type { Dayjs } from 'dayjs'
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'

import { labelSets } from '@/misc/graphicDataFormatter.js'
import { formatQuery, getUrlQuery } from '@/misc/string_utils'
import { useStore } from '@/store'

import AutomatableStaysStats from './AutomatableStaysStats.vue'
import FinancialStats from './FinancialStats.vue'
import GlobalStats from './GlobalStats.vue'
import NewCodingStaysStats from './NewCodingStaysStats.vue'
import OptimizableStaysStats from './OptimizableStaysStats.vue'
import StatsSettings from './StatsSettings.vue'
import { ChartUnit } from './types'

const store = useStore()
const router = useRouter()

const chartUnit = ref(ChartUnit.MONTH)

const globalStats = computed(() => store.state.stats.globalStats)
const earlyLimit = computed(() => store.state.settings.earlyLimit)
const lateLimit = computed(() => store.state.settings.lateLimit)
const selectedMedicalUnits = computed(() => store.state.settings.selectedMedicalUnits)
const isSancareUser = computed(() => store.state.login.currentUser.sancareUser)

const labelSetsComputed = computed(() => labelSets(globalStats.value, chartUnit.value))

function setDateRange({ earlyLimit, lateLimit }: { earlyLimit: Dayjs | null, lateLimit: Dayjs | null }) {
  store.commit('setEarlyLimit', earlyLimit)
  store.commit('setLateLimit', lateLimit)
  router.replace({
    path: store.state.route.path,
    query: {
      ...store.state.route.query,
      el: earlyLimit?.format('DD-MM-YYYY'),
      ll: lateLimit?.format('DD-MM-YYYY'),
    }
  })
}

function setChartUnit(unit: ChartUnit) {
  chartUnit.value = unit
}

function exportStats() {
  const urlArgs = {
    earlyLimit: earlyLimit.value,
    lateLimit: lateLimit.value,
    chartUnit: chartUnit.value,
    medicalUnits: selectedMedicalUnits.value,
  }
  const url = `/api/stats/export-csv?${getUrlQuery(urlArgs)}`

  window.open(url, '_blank')
}

function exportUsageStats() {
  const urlArgs = {
    earlyLimit: earlyLimit.value,
    lateLimit: lateLimit.value,
  }
  const url = `/api/stats/usage-csv?${getUrlQuery(urlArgs)}`

  window.open(url, '_blank')
}

function setSelectedMedicalUnits(medicalUnitList: string[]) {
  store.commit('setSelectedMedicalUnits', medicalUnitList)
  router.replace({
    path: store.state.route.path,
    query: formatQuery({ ...store.state.route.query, mu: medicalUnitList })
  })
}
</script>

<template>
  <div class="container">
    <stats-settings
      :early-limit="earlyLimit"
      :late-limit="lateLimit"
      :chart-unit="chartUnit"
      @set-date-range="setDateRange"
      @set-chart-unit="setChartUnit"
      @set-selected-medical-units="setSelectedMedicalUnits"
    />
    <div class="d-flex flex-row-reverse">
      <button
        class="btn btn-start-rss-pipeline"
        @click="exportStats()"
      >
        {{ $t('stats.page.buttonExportStatsLabel') }}
      </button>
    </div>
    <global-stats
      :early-limit="earlyLimit"
      :late-limit="lateLimit"
      :chart-unit="chartUnit"
      :label-sets="labelSetsComputed"
      :selected-medical-units="selectedMedicalUnits"
    />
    <financial-stats
      :early-limit="earlyLimit"
      :late-limit="lateLimit"
      :chart-unit="chartUnit"
      :label-sets="labelSetsComputed"
      :selected-medical-units="selectedMedicalUnits"
    />
    <automatable-stays-stats
      :early-limit="earlyLimit"
      :late-limit="lateLimit"
      :chart-unit="chartUnit"
      :selected-medical-units="selectedMedicalUnits"
      :label-sets="labelSetsComputed"
    />
    <new-coding-stays-stats
      :early-limit="earlyLimit"
      :late-limit="lateLimit"
      :chart-unit="chartUnit"
      :selected-medical-units="selectedMedicalUnits"
      :label-sets="labelSetsComputed"
    />
    <optimizable-stays-stats
      :early-limit="earlyLimit"
      :late-limit="lateLimit"
      :chart-unit="chartUnit"
      :selected-medical-units="selectedMedicalUnits"
      :label-sets="labelSetsComputed"
    />
    <div
      v-if="isSancareUser"
      class="stat"
    >
      <h1 class="stat-title" />
      <p
        class="m-2 fst-italic"
      >
        {{ $t('stats.page.warningExport') }}
      </p>
      <div class="d-flex m-2">
        <button
          class="btn btn-start-rss-pipeline"
          @click="exportUsageStats()"
        >
          {{ $t('stats.page.buttonExportUsageLabel') }}
        </button>
      </div>
    </div>
  </div>
</template>
